import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from 'extended-oxygen-elements';
import { ErrorBox } from 'element';
import { activeSitesAsSelectOptionsArraySelector } from 'state/AccountTree';
import { Api } from 'lib/Api';
import { LocationTabs, DutiesTable } from './components';
import {
  DayOfWeek,
  DutyConfiguration,
  DAYS_OF_WEEK,
} from './types';

// Inline constants to avoid module resolution issues
const COLORS = {
  primary: '#000000',
  secondary: '#545454',
  border: 'rgba(0, 0, 0, 0.23)',
} as const;

const CARD_STYLES = {
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2), 0px 0px 1px rgba(0, 0, 0, 0.24)',
} as const;

// Type for backend duty response
type BackendDuty = {
  id: string;
  external_id: number;
  name: string;
  description: string;
  min_personnel: number | null;
  time_slots: any | null;
  created_at: string;
  updated_at: string;
};

// Helper function to transform backend duty to frontend configuration
const transformDutyToConfiguration = (duty: BackendDuty): DutyConfiguration => ({
  duty_type: duty.id,
  duty_name: duty.name,
  is_enabled: false,
  time_periods: null,
  max_people: null,
});

// Helper function to create duty configurations for all days
const createDutiesForAllDays = (duties: DutyConfiguration[]): Record<DayOfWeek, DutyConfiguration[]> => {
  const result = {} as Record<DayOfWeek, DutyConfiguration[]>;
  DAYS_OF_WEEK.forEach(day => {
    result[day] = duties.map(duty => ({ ...duty }));
  });
  return result;
};

// Helper function to extract error message from API error
const getErrorMessage = (error: any): string => {
  return error?.response?.data?.message || 
         error?.response?.data?.error ||
         error?.message || 
         'Failed to load duty configurations. Please try again.';
};

export function DutiesByLocation() {
  const sitesDropDownOptions = useSelector(activeSitesAsSelectOptionsArraySelector);
  
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>('Monday');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [allDutiesData, setAllDutiesData] = useState<Record<DayOfWeek, DutyConfiguration[]> | null>(null);

  // Fetch duties callback - defined before useEffect that uses it
  const fetchDuties = useCallback(async () => {
    setIsLoading(true);
    setErrors([]);

    try {
      const response = await Api.Duties.getDuties();
      
      if (!response?.data) {
        setErrors(['No data received from API.']);
        return;
      }

      // API returns an array of duty types
      if (!Array.isArray(response.data)) {
        setErrors(['Unexpected response format. Expected an array of duties.']);
        return;
      }

      // Transform backend duties to frontend configurations
      const dutyConfigurations = response.data.map(transformDutyToConfiguration);
      
      // Create day-based structure (all days have the same duty types, all unconfigured)
      const dutiesByDay = createDutiesForAllDays(dutyConfigurations);
      
      setAllDutiesData(dutiesByDay);
    } catch (error: any) {
      setErrors([getErrorMessage(error)]);
      setAllDutiesData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize with the first location
  useEffect(() => {
    if (sitesDropDownOptions.length > 0 && !selectedLocationId) {
      setSelectedLocationId(sitesDropDownOptions[0].value);
    }
  }, [sitesDropDownOptions, selectedLocationId]);

  // Fetch duties on component mount
  useEffect(() => {
    fetchDuties();
  }, [fetchDuties]);

  // Get duties for the selected day (memoized)
  const duties = useMemo(() => {
    return allDutiesData?.[selectedDay] || [];
  }, [allDutiesData, selectedDay]);

  const handleLocationChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedLocationId(event.target.value);
    // TODO: When backend supports location-based duties, add API call here
  }, []);

  const handleDayChange = useCallback((day: DayOfWeek) => {
    setSelectedDay(day);
  }, []);

  const handleConfigure = useCallback((dutyType: string) => {
    // TODO: Navigate to or open duty configuration interface
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return (
    <Box sx={{ mt: -1 }}>
      {/* White Card Container - matches Figma design */}
      <Box
        sx={{
          ...CARD_STYLES,
          padding: { xs: '16px', sm: '24px' },
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Page Title - inside card */}
        <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
          Duties required by location
        </Typography>

        {/* Location Selector */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            component="label" 
            htmlFor="location-select"
            variant="subtitle2" 
            sx={{ mb: 0.5, fontWeight: 600, lineHeight: 1.2, display: 'block' }}
          >
            Location
          </Typography>
          <FormControl 
            fullWidth 
            sx={{ 
              maxWidth: { xs: '100%', sm: '400px' }, 
              mt: 0.5 
            }}
          >
            <Select
              id="location-select"
              value={selectedLocationId}
              onChange={handleLocationChange}
              displayEmpty
              aria-label="Select location"
              inputProps={{
                'aria-describedby': 'location-select-description',
              }}
              sx={{ 
                mt: 0,
                '& .MuiSelect-select': {
                  color: COLORS.primary,
                },
                '& .MuiSelect-icon': {
                  color: COLORS.secondary,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.border,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {sitesDropDownOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Error Display */}
        {errors.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ErrorBox errors={errors} clearErrors={clearErrors} />
          </Box>
        )}

        {/* Day of Week Tabs */}
        <LocationTabs selectedDay={selectedDay} onDayChange={handleDayChange} />

        {/* Duties Table */}
        <Box
          sx={{
            // Allow horizontal scroll on mobile if table is wider than container
            overflowX: 'auto',
            // Remove negative margin on mobile to prevent overflow
            mx: { xs: -2, sm: 0 },
            px: { xs: 2, sm: 0 },
          }}
        >
          <DutiesTable
            duties={duties}
            isLoading={isLoading}
            onConfigure={handleConfigure}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default DutiesByLocation;
